import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, DollarSign, Pencil, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import EditVehicleModal from "@/components/admin/EditVehicleModal";
import DeleteConfirmDialog from "@/components/admin/DeleteConfirmDialog";
import VehicleStatusChangeModal from "@/components/admin/VehicleStatusChangeModal";
import VehicleImageUploader from "@/components/admin/VehicleImageUploader";
import VehicleVideoUploader from "@/components/admin/VehicleVideoUploader";
import VehicleImagineSection from "@/components/admin/VehicleImagineSection";
import DescriptionPointsEditor, {
  cleanDescriptionPoints,
} from "@/components/admin/DescriptionPointsEditor";
import {
  galleryFromAssets,
  PLACEHOLDER_IMAGE_URL,
  placeholderImageAsset,
  realImageAssets,
  vehicleGallery,
  vehiclePrimaryImage,
  vehicleUsesPlaceholder,
  vehicleVideos,
  videoAssetsFromVehicle,
} from "@/utils/vehicleImages";
import { formatVehiclePrice } from "@/utils/vehiclePrice";
import { vehicleStatusClass, vehicleStatusLabel } from "@/utils/vehicleStatus";
import { formatDateMdY, formatDateTimeMdY } from "@/utils/dateFormat";
import { formatApiError } from "@/utils/apiError";
import { adminDialogPanel, adminOverlay } from "@/components/admin/adminUi";
import {
  askingPriceForDeal,
  ConvertLeadModal,
  CreateDealModal,
} from "@/components/admin/DealWorkflowModals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white px-3 py-2.5 font-['Manrope'] text-sm focus:outline-none focus:border-blue-500/50";
const selectCls = `${inputCls} [&>option]:bg-gray-900 [&>option]:text-white`;
const labelCls = "block text-xs font-['Manrope'] text-white/50 mb-1";

function display(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 py-2.5 border-b border-white/5">
      <div className="text-[11px] font-['Manrope'] text-white/45 uppercase tracking-wide">{label}</div>
      <div className="sm:col-span-2 text-sm font-['Manrope'] text-white break-words">{children}</div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input className={inputCls} {...props} />
    </div>
  );
}

function Section({ title, editing, onEdit, onCancel, onSave, saving, error, children }) {
  return (
    <section className="bg-white/5 border border-white/10 p-4 sm:p-5 self-start">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h2 className="text-sm font-['Unbounded'] font-semibold text-white pt-1.5">{title}</h2>
        {!editing ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit ${title}`}
            className="inline-flex items-center justify-center min-h-11 min-w-11 text-white/50 hover:text-white hover:bg-white/10"
          >
            <Pencil className="w-4 h-4" />
          </button>
        ) : (
          <span className="text-[11px] font-['Manrope'] text-blue-400 pt-2">Editing</span>
        )}
      </div>
      {editing && error && (
        <p className="text-sm font-['Manrope'] text-red-400 mb-3" role="alert">
          {error}
        </p>
      )}
      {children}
      {editing && (
        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="min-h-11 px-4 border border-white/15 text-white font-['Manrope'] hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="min-h-11 px-4 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </section>
  );
}

function daysInStock(vehicle) {
  const raw = vehicle?.created_at || vehicle?.date_in_stock || vehicle?.in_stock_date;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function assetsFromVehicle(vehicle) {
  if (!vehicle) return [];
  if (Array.isArray(vehicle.image_assets) && vehicle.image_assets.length > 0) {
    return vehicle.image_assets
      .map((a) =>
        typeof a === "string"
          ? { url: a, public_id: null }
          : { url: a.url, public_id: a.public_id || null },
      )
      .filter((a) => a.url);
  }
  return vehicleGallery(vehicle).map((url) => ({ url, public_id: null }));
}

function parseOptionalInt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

export default function AdminVehicleDetailPage() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [imageAssets, setImageAssets] = useState([]);
  const [imagesPending, setImagesPending] = useState(false);
  const [videoAssets, setVideoAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [sellNowOpen, setSellNowOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [createDealOpen, setCreateDealOpen] = useState(false);

  const fetchVehicle = useCallback(async () => {
    if (!token || !vehicleId) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicle(data);
      setNotFound(false);
      setActiveImage((i) => {
        const n = vehicleGallery(data).filter(Boolean).length;
        if (!n) return 0;
        return Math.min(i, n - 1);
      });
    } catch (error) {
      if (error.response?.status === 404) setNotFound(true);
      else toast.error(error.response?.data?.detail || "Failed to load vehicle");
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  }, [token, vehicleId]);

  useEffect(() => {
    if (isAuthenticated && token) fetchVehicle();
  }, [isAuthenticated, token, fetchVehicle]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setLightbox(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const gallery = useMemo(() => {
    if (!vehicle) return [];
    return vehicleGallery(vehicle).filter(Boolean);
  }, [vehicle]);

  const videos = useMemo(() => (vehicle ? vehicleVideos(vehicle) : []), [vehicle]);

  const copyVin = () => {
    if (!vehicle?.vin) return;
    navigator.clipboard?.writeText(vehicle.vin).then(
      () => toast.success("VIN copied"),
      () => toast.error("Could not copy VIN"),
    );
  };

  const toggleFeatured = async () => {
    if (!vehicle) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/vehicles/${vehicle.id}`,
        { featured: !vehicle.featured },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(vehicle.featured ? "Unfeatured" : "Featured");
      fetchVehicle();
    } catch {
      toast.error("Failed to update featured");
    }
  };

  const handleDelete = async () => {
    if (!vehicle) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/vehicles/${vehicle.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Vehicle deleted");
      navigate("/admin/inventory");
    } catch {
      toast.error("Failed to delete vehicle");
    }
  };

  const startEdit = (section) => {
    if (!vehicle) return;
    setSaveError("");
    setEditing(section);
    if (section === "pricing") {
      setDraft({
        status: vehicle.status || "available",
        price: vehicle.price_not_decided ? "" : (vehicle.price ?? ""),
        price_not_decided: Boolean(vehicle.price_not_decided),
        featured: Boolean(vehicle.featured),
        list_at_end: Boolean(vehicle.list_at_end),
      });
    } else if (section === "specs") {
      setDraft({
        year: vehicle.year ?? "",
        make: vehicle.make || "",
        model: vehicle.model || "",
        trim: vehicle.trim || "",
        body_type: vehicle.body_type || "",
        vin: vehicle.vin || "",
        stockNumber: vehicle.stockNumber || "",
        mileage: vehicle.mileage ?? "",
        engine: vehicle.engine || "",
        transmission: vehicle.transmission || "Automatic",
        drivetrain: vehicle.drivetrain || "",
        cylinders: vehicle.cylinders || "",
        fuel_type: vehicle.fuel_type || "Gasoline",
        city_mpg: vehicle.city_mpg ?? "",
        hwy_mpg: vehicle.hwy_mpg ?? "",
        exterior_color: vehicle.exterior_color || "",
        interior_color: vehicle.interior_color || "",
        condition: vehicle.condition || "",
      });
    } else if (section === "acquisition") {
      setDraft({
        purchase_source: vehicle.purchase_source || vehicle.source || "",
        purchase_date: (vehicle.purchase_date || "").slice(0, 10),
        purchase_price: vehicle.purchase_price ?? "",
        lot_location: vehicle.lot_location || vehicle.location || "",
        recon_status: vehicle.recon_status || "",
      });
    } else if (section === "features") {
      setDraft({
        features: Array.isArray(vehicle.features) ? vehicle.features.join(", ") : (vehicle.features || ""),
        description_points: Array.isArray(vehicle.description_points) ? [...vehicle.description_points] : [],
        description: vehicle.description || "",
        internal_notes: vehicle.internal_notes || vehicle.notes || "",
      });
    } else if (section === "media") {
      const loaded = realImageAssets(assetsFromVehicle(vehicle));
      setImageAssets(loaded);
      setImagesPending(vehicleUsesPlaceholder(vehicle) && loaded.length === 0);
      setVideoAssets(videoAssetsFromVehicle(vehicle));
      setDraft({});
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft({});
    setSaveError("");
    setSaving(false);
  };

  const saveSection = async (payload) => {
    if (!vehicle) return;
    setSaving(true);
    setSaveError("");
    try {
      await axios.put(`${BACKEND_URL}/api/vehicles/${vehicle.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Saved");
      setEditing(null);
      setDraft({});
      await fetchVehicle();
    } catch (error) {
      setSaveError(formatApiError(error, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const savePricing = () => {
    const priceNotDecided = Boolean(draft.price_not_decided);
    const price = priceNotDecided ? 0 : parseInt(draft.price, 10);
    if (!priceNotDecided && (!Number.isFinite(price) || price <= 0)) {
      setSaveError("Enter a price or check Price not decided.");
      return;
    }
    return saveSection({
      status: draft.status,
      price,
      price_not_decided: priceNotDecided,
      featured: Boolean(draft.featured),
      list_at_end: Boolean(draft.list_at_end),
    });
  };

  const saveSpecs = () => {
    const year = parseInt(draft.year, 10);
    const mileage = parseInt(draft.mileage, 10);
    const vin = String(draft.vin || "").trim().toUpperCase();
    if (!draft.make?.trim() || !draft.model?.trim()) {
      setSaveError("Make and model are required.");
      return;
    }
    if (!Number.isFinite(year)) {
      setSaveError("Year is required.");
      return;
    }
    if (vin.length !== 17) {
      setSaveError("VIN must be 17 characters.");
      return;
    }
    if (!Number.isFinite(mileage) || mileage < 0) {
      setSaveError("Mileage is required.");
      return;
    }
    return saveSection({
      year,
      make: draft.make.trim(),
      model: draft.model.trim(),
      trim: draft.trim.trim() || null,
      body_type: draft.body_type.trim() || null,
      vin,
      stockNumber: draft.stockNumber.trim() || null,
      mileage,
      engine: draft.engine.trim(),
      transmission: draft.transmission,
      drivetrain: draft.drivetrain.trim() || null,
      cylinders: draft.cylinders.trim() || null,
      fuel_type: draft.fuel_type,
      city_mpg: parseOptionalInt(draft.city_mpg),
      hwy_mpg: parseOptionalInt(draft.hwy_mpg),
      exterior_color: draft.exterior_color.trim(),
      interior_color: draft.interior_color.trim(),
      condition: draft.condition.trim() || null,
    });
  };

  const saveAcquisition = () => {
    const purchasePrice = parseOptionalInt(draft.purchase_price);
    return saveSection({
      purchase_source: draft.purchase_source.trim() || "",
      purchase_date: draft.purchase_date || "",
      purchase_price: purchasePrice,
      lot_location: draft.lot_location.trim() || "",
      recon_status: draft.recon_status.trim() || "",
    });
  };

  const saveFeatures = () => {
    const features = String(draft.features || "")
      .split(",")
      .map((f) => f.trim())
      .filter(Boolean);
    return saveSection({
      features,
      description_points: cleanDescriptionPoints(draft.description_points),
      description: draft.description.trim() || "",
      internal_notes: draft.internal_notes.trim() || "",
    });
  };

  const saveMedia = () => {
    const photos = realImageAssets(imageAssets);
    if (!photos.length && !imagesPending) {
      setSaveError("Please keep at least one vehicle photo.");
      return;
    }
    const pendingWithoutPhotos = imagesPending && !photos.length;
    const savedAssets = pendingWithoutPhotos ? [placeholderImageAsset()] : photos;
    const images = galleryFromAssets(savedAssets);
    return saveSection({
      images_pending: pendingWithoutPhotos,
      image: images[0] || (pendingWithoutPhotos ? PLACEHOLDER_IMAGE_URL : undefined),
      images,
      image_assets: savedAssets,
      videos: galleryFromAssets(videoAssets),
      video_assets: videoAssets,
    });
  };

  const setField = (key) => (e) => {
    const { type, checked, value } = e.target;
    setDraft((d) => ({ ...d, [key]: type === "checkbox" ? checked : value }));
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <div className="h-8 w-48 bg-white/10 animate-pulse mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div className="aspect-video bg-white/10 animate-pulse" />
          <div className="h-40 bg-white/10 animate-pulse" />
        </div>
      </div>
    );
  }

  if (notFound || !vehicle) {
    return (
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-vehicle-not-found">
        <button
          type="button"
          onClick={() => navigate("/admin/inventory")}
          className="inline-flex items-center gap-2 min-h-11 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Inventory
        </button>
        <p className="text-white font-['Unbounded'] text-xl mb-2">Vehicle not found</p>
        <p className="text-white/50 font-['Manrope'] text-sm">This listing may have been deleted or the link is invalid.</p>
      </div>
    );
  }

  const title = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ");
  const mainSrc = gallery[activeImage] || vehiclePrimaryImage(vehicle);
  const placeholder = !mainSrc || vehicleUsesPlaceholder(vehicle);
  const features = Array.isArray(vehicle.features)
    ? vehicle.features
    : String(vehicle.features || "")
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean);
  const points = Array.isArray(vehicle.description_points)
    ? vehicle.description_points.filter((p) => typeof p === "string" && p.trim())
    : [];
  const days = daysInStock(vehicle);
  const mpg = [vehicle.city_mpg, vehicle.hwy_mpg].some((n) => n)
    ? `${vehicle.city_mpg || "—"} / ${vehicle.hwy_mpg || "—"} city/hwy`
    : "—";

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-vehicle-detail">
      <button
        type="button"
        onClick={() => navigate("/admin/inventory")}
        className="inline-flex items-center gap-2 min-h-11 text-sm font-['Manrope'] text-white/50 hover:text-white mb-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Inventory
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-['Unbounded'] font-bold text-white">{title}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm font-['Manrope'] text-white/50">
            <span>Stock {display(vehicle.stockNumber)}</span>
            <span className="text-white/20">·</span>
            <button
              type="button"
              onClick={copyVin}
              className="inline-flex items-center gap-1 min-h-11 hover:text-white"
              title="Copy VIN"
            >
              VIN {display(vehicle.vin)}
              {vehicle.vin && <Copy className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => setStatusOpen(true)}
              className={`px-2 py-1 text-[10px] font-semibold border ${vehicleStatusClass(vehicle.status)}`}
            >
              {vehicleStatusLabel(vehicle.status)}
            </button>
            {vehicle.featured && (
              <span className="inline-flex items-center gap-1 text-yellow-400 text-xs">
                <Star className="w-3.5 h-3.5 fill-yellow-400" /> Featured
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          <button
            type="button"
            onClick={() => setSellNowOpen(true)}
            className="min-h-11 inline-flex items-center justify-center gap-2 px-4 bg-emerald-500 hover:bg-emerald-600 text-white font-['Manrope'] font-semibold"
            data-testid="vehicle-detail-sell-now"
          >
            <DollarSign className="w-4 h-4" />
            Sell Now
          </button>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="min-h-11 inline-flex items-center justify-center gap-2 px-4 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold"
            data-testid="vehicle-detail-edit"
          >
            <Pencil className="w-4 h-4" />
            Edit Vehicle
          </button>
          <button
            type="button"
            onClick={() => setStatusOpen(true)}
            className="min-h-11 px-4 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
          >
            Change status
          </button>
          <button
            type="button"
            onClick={toggleFeatured}
            className="min-h-11 px-4 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
          >
            {vehicle.featured ? "Unfeature" : "Feature"}
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="min-h-11 px-4 border border-red-500/30 text-red-400 font-['Manrope'] hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4 inline mr-1" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        <div className="contents lg:flex lg:flex-col lg:gap-4 lg:min-w-0">
        <div className="space-y-3 order-1">
          <button
            type="button"
            onClick={() => !placeholder && setLightbox(true)}
            className="block w-full aspect-video bg-[#0a0a0a] border border-white/10 overflow-hidden"
          >
            {placeholder ? (
              <div className="w-full h-full flex items-center justify-center text-white/40 font-['Unbounded'] text-sm px-4">
                {title}
              </div>
            ) : (
              <img src={mainSrc} alt={title} className="w-full h-full object-cover" />
            )}
          </button>
          {gallery.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {gallery.map((url, idx) => (
                <button
                  key={`${url}-${idx}`}
                  type="button"
                  onClick={() => setActiveImage(idx)}
                  className={`shrink-0 w-20 h-14 overflow-hidden border ${
                    idx === activeImage ? "border-blue-400" : "border-white/10"
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="order-4">
          <Section
            title="Acquisition / Lot"
            editing={editing === "acquisition"}
            onEdit={() => startEdit("acquisition")}
            onCancel={cancelEdit}
            onSave={saveAcquisition}
            saving={saving}
            error={editing === "acquisition" ? saveError : ""}
          >
            {editing === "acquisition" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Purchase source" value={draft.purchase_source} onChange={setField("purchase_source")} />
                <Input label="Purchase date" type="date" value={draft.purchase_date} onChange={setField("purchase_date")} />
                <Input label="Purchase price ($)" type="number" min="0" value={draft.purchase_price} onChange={setField("purchase_price")} />
                <Input label="Lot location" value={draft.lot_location} onChange={setField("lot_location")} />
                <Input label="Recon status" value={draft.recon_status} onChange={setField("recon_status")} />
              </div>
            ) : (
              <>
                <Field label="Purchase source">{display(vehicle.purchase_source)}</Field>
                <Field label="Purchase date">
                  {vehicle.purchase_date ? formatDateMdY(vehicle.purchase_date) : "—"}
                </Field>
                <Field label="Purchase price">
                  {vehicle.purchase_price != null && vehicle.purchase_price !== ""
                    ? `$${Number(vehicle.purchase_price).toLocaleString()}`
                    : "—"}
                </Field>
                <Field label="Lot location">{display(vehicle.lot_location)}</Field>
                <Field label="Recon status">{display(vehicle.recon_status)}</Field>
                <Field label="Linked deals">
                  {Number(vehicle.linked_deal_count) > 0 ? vehicle.linked_deal_count : "—"}
                </Field>
              </>
            )}
          </Section>
        </div>

        <div className="order-5">
          <Section
            title="Features / Description"
            editing={editing === "features"}
            onEdit={() => startEdit("features")}
            onCancel={cancelEdit}
            onSave={saveFeatures}
            saving={saving}
            error={editing === "features" ? saveError : ""}
          >
            {editing === "features" ? (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Features (comma-separated)</label>
                  <textarea className={`${inputCls} resize-y min-h-[5rem]`} value={draft.features} onChange={setField("features")} />
                </div>
                <DescriptionPointsEditor
                  points={draft.description_points}
                  onChange={(next) => setDraft((d) => ({ ...d, description_points: next }))}
                  disabled={saving}
                />
                <div>
                  <label className={labelCls}>Public description</label>
                  <textarea className={`${inputCls} resize-y min-h-[5rem]`} value={draft.description} onChange={setField("description")} />
                </div>
                <div>
                  <label className={labelCls}>Internal notes</label>
                  <textarea className={`${inputCls} resize-y min-h-[5rem]`} value={draft.internal_notes} onChange={setField("internal_notes")} />
                </div>
              </div>
            ) : (
              <>
                <Field label="Features">
                  {features.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {features.map((f) => (
                        <span key={f} className="px-2 py-0.5 text-xs border border-white/15 text-white/80">
                          {f}
                        </span>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Description points">
                  {points.length ? (
                    <ul className="list-disc pl-4 space-y-1 text-white/80">
                      {points.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Description">{display(vehicle.description)}</Field>
                <Field label="Internal notes">{display(vehicle.internal_notes)}</Field>
              </>
            )}
          </Section>
        </div>
        </div>

        <div className="contents lg:flex lg:flex-col lg:gap-4 lg:min-w-0">
        <div className="order-2">
          <Section
            title="Pricing & Status"
            editing={editing === "pricing"}
            onEdit={() => startEdit("pricing")}
            onCancel={cancelEdit}
            onSave={savePricing}
            saving={saving}
            error={editing === "pricing" ? saveError : ""}
          >
            {editing === "pricing" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Status</label>
                  <select className={selectCls} value={draft.status} onChange={setField("status")}>
                    <option value="available">Available</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <Input
                  label="Asking price ($)"
                  type="number"
                  min="0"
                  disabled={draft.price_not_decided}
                  value={draft.price}
                  onChange={setField("price")}
                />
                <label className="flex items-center gap-2 text-sm font-['Manrope'] text-white/80 min-h-11">
                  <input type="checkbox" checked={draft.price_not_decided} onChange={setField("price_not_decided")} className="w-5 h-5" />
                  Price not decided
                </label>
                <label className="flex items-center gap-2 text-sm font-['Manrope'] text-white/80 min-h-11">
                  <input type="checkbox" checked={draft.featured} onChange={setField("featured")} className="w-5 h-5" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm font-['Manrope'] text-white/80 min-h-11">
                  <input type="checkbox" checked={draft.list_at_end} onChange={setField("list_at_end")} className="w-5 h-5" />
                  List at end
                </label>
              </div>
            ) : (
              <>
                <Field label="Asking price">{formatVehiclePrice(vehicle) || "Price TBD"}</Field>
                <Field label="Price not decided">{vehicle.price_not_decided ? "Yes" : "No"}</Field>
                <Field label="Status">{vehicleStatusLabel(vehicle.status)}</Field>
                <Field label="Featured">{vehicle.featured ? "Yes" : "No"}</Field>
                <Field label="List at end">{vehicle.list_at_end ? "Yes" : "No"}</Field>
                <Field label="Days in inventory">{days != null ? days : "—"}</Field>
                <Field label="Created">{vehicle.created_at ? formatDateTimeMdY(vehicle.created_at) : "—"}</Field>
                <Field label="Updated">{vehicle.updated_at ? formatDateTimeMdY(vehicle.updated_at) : "—"}</Field>
                <Field label="Sold date">{vehicle.sold_at ? formatDateMdY(vehicle.sold_at) : "—"}</Field>
                <Field label="Sold price">
                  {vehicle.sale?.sale_price != null
                    ? `$${Number(vehicle.sale.sale_price).toLocaleString()}`
                    : "—"}
                </Field>
              </>
            )}
          </Section>
        </div>

        <div className="order-3">
          <Section
            title="Specs"
            editing={editing === "specs"}
            onEdit={() => startEdit("specs")}
            onCancel={cancelEdit}
            onSave={saveSpecs}
            saving={saving}
            error={editing === "specs" ? saveError : ""}
          >
            {editing === "specs" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Year" type="number" value={draft.year} onChange={setField("year")} />
                <Input label="Make" value={draft.make} onChange={setField("make")} />
                <Input label="Model" value={draft.model} onChange={setField("model")} />
                <Input label="Trim" value={draft.trim} onChange={setField("trim")} />
                <Input label="Body style" value={draft.body_type} onChange={setField("body_type")} />
                <Input label="VIN" value={draft.vin} maxLength={17} onChange={setField("vin")} />
                <Input label="Stock #" value={draft.stockNumber} onChange={setField("stockNumber")} />
                <Input label="Mileage" type="number" min="0" value={draft.mileage} onChange={setField("mileage")} />
                <Input label="Engine" value={draft.engine} onChange={setField("engine")} />
                <div>
                  <label className={labelCls}>Transmission</label>
                  <select className={selectCls} value={draft.transmission} onChange={setField("transmission")}>
                    <option value="Automatic">Automatic</option>
                    <option value="Manual">Manual</option>
                    <option value="CVT">CVT</option>
                    <option value="DCT">DCT</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Drivetrain</label>
                  <select className={selectCls} value={draft.drivetrain} onChange={setField("drivetrain")}>
                    <option value="">—</option>
                    <option value="FWD">FWD</option>
                    <option value="RWD">RWD</option>
                    <option value="AWD">AWD</option>
                    <option value="4WD">4WD</option>
                  </select>
                </div>
                <Input label="Cylinders" value={draft.cylinders} onChange={setField("cylinders")} />
                <div>
                  <label className={labelCls}>Fuel</label>
                  <select className={selectCls} value={draft.fuel_type} onChange={setField("fuel_type")}>
                    <option value="Gasoline">Gasoline</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Electric">Electric</option>
                    <option value="Diesel">Diesel</option>
                  </select>
                </div>
                <Input label="City MPG" type="number" min="1" value={draft.city_mpg} onChange={setField("city_mpg")} />
                <Input label="Highway MPG" type="number" min="1" value={draft.hwy_mpg} onChange={setField("hwy_mpg")} />
                <Input label="Exterior color" value={draft.exterior_color} onChange={setField("exterior_color")} />
                <Input label="Interior color" value={draft.interior_color} onChange={setField("interior_color")} />
                <div>
                  <label className={labelCls}>Condition</label>
                  <select className={selectCls} value={draft.condition} onChange={setField("condition")}>
                    <option value="">—</option>
                    <option value="New">New</option>
                    <option value="Used">Used</option>
                    <option value="Certified Pre-Owned">Certified Pre-Owned</option>
                  </select>
                </div>
              </div>
            ) : (
              <>
                <Field label="Year">{display(vehicle.year)}</Field>
                <Field label="Make">{display(vehicle.make)}</Field>
                <Field label="Model">{display(vehicle.model)}</Field>
                <Field label="Trim">{display(vehicle.trim)}</Field>
                <Field label="Body style">{display(vehicle.body_type)}</Field>
                <Field label="VIN">{display(vehicle.vin)}</Field>
                <Field label="Stock #">{display(vehicle.stockNumber)}</Field>
                <Field label="Mileage">
                  {vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString()} mi` : "—"}
                </Field>
                <Field label="Engine">{display(vehicle.engine)}</Field>
                <Field label="Transmission">{display(vehicle.transmission)}</Field>
                <Field label="Drivetrain">{display(vehicle.drivetrain)}</Field>
                <Field label="Cylinders">{display(vehicle.cylinders)}</Field>
                <Field label="Fuel">{display(vehicle.fuel_type)}</Field>
                <Field label="MPG">{mpg}</Field>
                <Field label="Exterior color">{display(vehicle.exterior_color)}</Field>
                <Field label="Interior color">{display(vehicle.interior_color)}</Field>
                <Field label="Condition">{display(vehicle.condition)}</Field>
              </>
            )}
          </Section>
        </div>
        </div>

        <div className="order-6 lg:col-span-2">
          <Section
            title="Media"
            editing={editing === "media"}
            onEdit={() => startEdit("media")}
            onCancel={cancelEdit}
            onSave={saveMedia}
            saving={saving}
            error={editing === "media" ? saveError : ""}
          >
            {editing === "media" ? (
              <div className="space-y-4">
                <VehicleImageUploader
                  assets={imageAssets}
                  onChange={setImageAssets}
                  token={token}
                  disabled={saving}
                  imagesPending={imagesPending}
                  onImagesPendingChange={setImagesPending}
                />
                <VehicleVideoUploader
                  assets={videoAssets}
                  onChange={setVideoAssets}
                  token={token}
                  disabled={saving}
                />
              </div>
            ) : (
              <>
                <Field label="Photos">{gallery.length || "—"}</Field>
                <Field label="Videos">{videos.length || "—"}</Field>
                <Field label="Photos pending">{vehicle.images_pending ? "Yes" : "No"}</Field>
                {videos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {videos.map((vid, idx) => (
                      <video
                        key={vid.public_id || vid.url || idx}
                        src={vid.url}
                        controls
                        className="w-full max-w-xl bg-black"
                        poster={vid.thumbnail_url || undefined}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </Section>
        </div>

        <div className="order-7 lg:col-span-2">
          <VehicleImagineSection vehicle={vehicle} token={token} onApplied={fetchVehicle} />
        </div>
      </div>

      {lightbox && !placeholder && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <img src={mainSrc} alt={title} className="max-h-[90dvh] max-w-full object-contain" />
        </div>
      )}

      {sellNowOpen && (
        <div className={adminOverlay("z-[60]")} data-testid="sell-now-choice-modal">
          <div className={adminDialogPanel("max-w-md")}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-lg font-['Unbounded'] font-bold text-white">Sell Now</h2>
              <button
                type="button"
                onClick={() => setSellNowOpen(false)}
                className="text-white/40 hover:text-white text-xl"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm font-['Manrope'] text-white/55">
                Who is buying this vehicle?
              </p>
              <button
                type="button"
                onClick={() => {
                  setSellNowOpen(false);
                  setConvertOpen(true);
                }}
                data-testid="sell-now-existing-lead"
                className="w-full min-h-11 px-4 py-3 bg-white/5 border border-white/10 text-left text-white font-['Manrope'] hover:bg-white/10"
              >
                1. Sell to Existing Lead
              </button>
              <button
                type="button"
                onClick={() => {
                  setSellNowOpen(false);
                  setCreateDealOpen(true);
                }}
                data-testid="sell-now-new-customer"
                className="w-full min-h-11 px-4 py-3 bg-white/5 border border-white/10 text-left text-white font-['Manrope'] hover:bg-white/10"
              >
                2. Sell to New Customer
              </button>
            </div>
            <div className="flex justify-end p-5 border-t border-white/10">
              <button
                type="button"
                onClick={() => setSellNowOpen(false)}
                className="min-h-11 px-4 border border-white/10 text-white/60 text-sm font-['Manrope'] hover:bg-white/5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {convertOpen && (
        <ConvertLeadModal
          token={token}
          initialVehicleId={vehicle.id}
          initialSalePrice={askingPriceForDeal(vehicle)}
          initialVehicle={vehicle}
          onClose={() => setConvertOpen(false)}
          onSuccess={(dealId) => {
            setConvertOpen(false);
            toast.success("Deal created successfully");
            navigate(`/admin/deals/${dealId}`);
          }}
        />
      )}

      {createDealOpen && (
        <CreateDealModal
          token={token}
          initialVehicleId={vehicle.id}
          initialSalePrice={askingPriceForDeal(vehicle)}
          initialVehicle={vehicle}
          onClose={() => setCreateDealOpen(false)}
          onSuccess={(dealId) => {
            setCreateDealOpen(false);
            toast.success("Deal created successfully");
            navigate(`/admin/deals/${dealId}`);
          }}
        />
      )}

      <EditVehicleModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={() => {
          setEditOpen(false);
          fetchVehicle();
        }}
        vehicle={vehicle}
        token={token}
      />
      <DeleteConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        vehicleName={title}
      />
      <VehicleStatusChangeModal
        isOpen={statusOpen}
        vehicle={vehicle}
        token={token}
        mode="inventory"
        onClose={() => setStatusOpen(false)}
        onSuccess={() => {
          setStatusOpen(false);
          fetchVehicle();
        }}
      />
    </div>
  );
}
